import { createContext, useContext, useState } from 'react';

/**
 * React Context to manage and share credit amount across the app.
 *
 * @property creditAmount - Current credit amount value.
 * @property setCreditAmount - Function to update the credit amount.
 */
const CreditAmountContext = createContext<{
  creditAmount: number;
  setCreditAmount: (amount: number) => void;
  handlePlaceOrder?: () => void;
  setHandlePlaceOrder?: (fn: () => void) => void;
}>({ creditAmount: 0, setCreditAmount: () => {} });

/**
 * Provider component that wraps parts of the app needing access
 * to the credit amount state.
 *
 * @param children - React components that will consume the context.
 *
 * @returns Context Provider with creditAmount state and updater.
 *
 * @example
 * <CreditAmountProvider>
 *    <YourComponent />
 * </CreditAmountProvider>
 */
export const CreditAmountProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [creditAmount, setCreditAmount] = useState(0);
  const [handlePlaceOrder, setHandlePlaceOrder] = useState<() => void>(
    () => () => {},
  );

  return (
    <CreditAmountContext.Provider
      value={{
        creditAmount,
        setCreditAmount,
        handlePlaceOrder,
        setHandlePlaceOrder,
      }}
    >
      {children}
    </CreditAmountContext.Provider>
  );
};

/**
 * Custom hook to access and update credit amount from context.
 *
 * @returns {Object} Contains:
 *  - creditAmount: number
 *  - setCreditAmount: (amount: number) => void
 *
 * @example
 * const { creditAmount, setCreditAmount } = useCreditAmount();
 * setCreditAmount(100);
 */
export const useCreditAmount = () => useContext(CreditAmountContext);
